import styled from '@emotion/styled'
import { getHumanDateFromTimestamp } from '@pubstack/common/src/date'
import { MarketplaceBidder } from '@pubstack/common/src/marketplaceBidder'
import { useAtom, useAtomValue } from 'jotai'
import { FunctionComponent, useEffect, useState } from 'react'
import { useIntegrationMarketplace, useMarketplaceRequestEmailQuery } from '~/api/api.hook'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Sizes } from '~/assets/style/tokens'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { CatalogWrapper } from '~/components/_styled/catalog'
import Button from '~/components/Button'
import { ModuleCard } from '~/components/catalog/ModuleCard'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { PageContent } from '~/components/PageContent'
import { useToast } from '~/components/Toast/useToasts'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { ExtraCatalogItemHeaderItem } from '~/feature-components/ModuleHeader'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { configurationState, currentScopeState } from '~/state'
import { isProd } from '~/utils/environment'
import { IntegrationConfirmLegalModal } from './IntegrationMarketplaceConfirmLegalModal'
import { PureMarketplaceHelpFlyout } from './PureMarketplaceHelpFlyout'
import { PureMarketplaceLegalsFlyout } from './PureMarketplaceLegalsFlyout'

const MarketplaceTeaser = styled.div`
  background: linear-gradient(90deg, rgba(0, 48, 207, 0.08) 13.95%, rgba(40, 191, 182, 0.08) 107.15%);
  border-radius: ${Sizes[4]};
  border: 1px solid ${Colors.King};
  flex-direction: column;
  align-items: center;
  padding: 10px ${Sizes[12]};
  ${Fonts.P2};
  margin-bottom:20px;
  h2 {
    display: flex;
    gap: 4px;
  }
  ul {
    padding-left: 20px;
    margin-top: 8px;
    margin-bottom: 8px;
  }
`
const Actions = styled.div`
  display: flex;
  justify-content: space-between;
`

const ModuleFooters = styled.div`
    display: flex;
    gap: 0.5rem;
`

const ToggleButton = styled.div`
    display: flex;
    gap:4px;
`

const HelpButton = styled(Button)`
  padding: 0;
`

type MarketPlaceHeaderItem = Pick<ExtraCatalogItemHeaderItem, 'labelInfo' | 'value'> & {
  onClick: () => void
}

export const MarketplacePage: FunctionComponent = () => {
  // State
  const [marketplaceBidders, setMarketplaceBidders] = useState<MarketplaceBidder[]>([])

  // Hooks
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const breadcrumbs = useBreadcrumbs()
  const [currentScope, setCurrentScope] = useAtom(currentScopeState)
  // Derived from the current scope (previously synced via an effect).
  const emailSentAt = currentScope.requestMarketplaceDate ? getHumanDateFromTimestamp(currentScope.requestMarketplaceDate, 'dd LLL. yyyy') : undefined
  const user = useAuthenticatedUser()
  const toast = useToast()
  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()

  // API Hooks
  const { all: allMarketplaceBidders, byId: marketplaceBidder } = useIntegrationMarketplace(null)
  const { byId: sendMarketplaceRequestEmail } = useMarketplaceRequestEmailQuery(null)

  const onRequestSend = async () => {
    if (sendMarketplaceRequestEmail.loading) {
      sendMarketplaceRequestEmail.abort()
    }
    const requestSent = await sendMarketplaceRequestEmail.put()

    setCurrentScope((scope) => ({ ...scope, requestMarketplaceDate: requestSent.requestMarketplaceDate }))
    toast.success(`Thank you for your interest ! Our team will get in touch with you as soon as possible.`)
  }

  const onActivateMarketplace = async (bidder: MarketplaceBidder) => {
    try {
      await marketplaceBidder.post({ ...bidder, activated: !bidder.activated })
      toast.success(bidder.activated ? `Marketplace has been deactivated on ${bidder.name}` : `Marketplace is active on ${bidder.name} ! Our team will contact you soon to set the details !`)
      getAllMarketplaceBidders()
    } catch (exception) {
      toast.alert(`The marketplace could not be updated for ${bidder.name}. Please try later or contact Pubstack support.`)
      throw exception
    }
  }

  const hasMarketplaceTeaser = user.hasFeature('marketplaceTeaser')
  const hasMarketPlaceFeature = user.hasFeature('marketplace')

  const marketplaceFeature = hasMarketPlaceFeature && hasMarketplaceTeaser ? 'full' : hasMarketPlaceFeature ? 'full' : hasMarketplaceTeaser ? 'teaser' : null
  const canActivateMarketplace = user.getScopeRole() === 'owner' || (user.view === 'owner' && !isProd())

  const useMarketplaceHeaderItems = (bidder: MarketplaceBidder): MarketPlaceHeaderItem => {
    switch (marketplaceFeature) {
      case 'teaser': {
        const onClick = () => {
          flyout.open(
            PureMarketplaceHelpFlyout,
            { color: Colors.Ming, width: `612px`, closeTrackingLabel: 'marketplace-teaser-help' },
            {
              requestSentAt: emailSentAt,
              onContact: () => {
                onRequestSend()
                flyout.close()
              },
            }
          )
        }
        return {
          value: (
            <Link
              icon={'chevronRightCircle'}
              label={'Learn more'}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onClick()
              }}
              trackingLabel={`marketplace-teaser-help`}
            />
          ),
          onClick,
        }
      }
      case 'full': {
        const onClick = () => {
          flyout.open(
            PureMarketplaceLegalsFlyout,
            { closeTrackingLabel: 'marketplace-bidder-legals-close', color: Colors.Ming },
            {
              bidderCode: bidder.id,
              bidderDisplayName: bidder.name,
              marketplaceProperties: bidder.marketplace,
            }
          )
        }
        return {
          labelInfo: (
            <HelpButton
              variant={'tertiary'}
              iconName={'help'}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onClick()
              }}
              size={'s'}
              trackingLabel={'marketplace-bidder-legals-view'}
            />
          ),
          value: (
            <ToggleButton>
              <Tooltip title={'Only an owner can enable Marketplace'} disabled={canActivateMarketplace}>
                <Toggle
                  value={bidder.activated}
                  disabled={!canActivateMarketplace}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    if (bidder.activated) {
                      onActivateMarketplace && onActivateMarketplace({ ...bidder, activated: bidder.activated })
                    } else {
                      modal.open(IntegrationConfirmLegalModal, {
                        marketplaceProperties: bidder.marketplace,
                        bidderCode: bidder.id,
                        bidderDisplayName: bidder.name,
                        onActivateMarketplace,
                      })
                    }
                  }}
                  trackingLabel={bidder.activated ? `marketplace-activation-${bidder.id}-unset` : `marketplace-activation-${bidder.id}-set`}
                />
              </Tooltip>
              {bidder.activated ? 'Enabled' : 'Available'}
            </ToggleButton>
          ),
          onClick,
        }
      }
      default:
        return {} as MarketPlaceHeaderItem
    }
  }

  const marketPlaceHeaders = marketplaceBidders.reduce(
    (acc, bidder) => {
      acc[bidder.id] = useMarketplaceHeaderItems(bidder)
      return acc
    },
    {} as Record<string, MarketPlaceHeaderItem>
  )

  const getAllMarketplaceBidders = async () => {
    if (allMarketplaceBidders.loading) {
      allMarketplaceBidders.abort()
    }
    const result = await allMarketplaceBidders.get()
    setMarketplaceBidders(result)
  }

  useEffect(() => {
    if (hasMarketplaceTeaser || hasMarketPlaceFeature) {
      getAllMarketplaceBidders()
    }
  }, [])

  return (
    <PageContent breadcrumbs={breadcrumbs}>
      {hasMarketplaceTeaser && !hasMarketPlaceFeature && (
        <MarketplaceTeaser>
          <h2>
            <Icon name={'store_front'} />
            Introducing Pubstack Marketplace!
          </h2>
          <em>Unlock the full potential of your ad inventory without the administrative headaches</em>
          <ul>
            <li>
              <strong>Global bidder access:</strong> Instantly connect with our global network of premium partners
            </li>
            <li>
              <strong>Simplified legal process:</strong> One contract covering all bidder partnerships and full compliance with GDPR and other regulations
            </li>
            <li>
              <strong>Unified billing solution:</strong> Payments streamlined into one payment for all partner bidders
            </li>
            <li>
              <strong>Easy setup and operational efficiency:</strong> Empower adops and avoid complex technical setups through automated bidder configuration and optimisation all managed by Pubstack
            </li>
          </ul>
          <Actions>
            <Link
              icon={'chevronRightCircle'}
              label={'Learn more'}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                flyout.open(
                  PureMarketplaceHelpFlyout,
                  { color: Colors.Ming, width: `612px`, closeTrackingLabel: 'marketplace-teaser-help' },
                  {
                    requestSentAt: emailSentAt,
                    onContact: () => {
                      onRequestSend()
                      flyout.close()
                    },
                  }
                )
              }}
              trackingLabel={`marketplace-teaser-help`}
            />
            {emailSentAt ? (
              <span>Last request sent {emailSentAt}</span>
            ) : (
              <Button disabled={sendMarketplaceRequestEmail.loading} variant={'secondary'} onClick={onRequestSend} trackingLabel={'marketplace-teaser-request'}>
                {sendMarketplaceRequestEmail.loading ? 'Sending request...' : 'Contact me'}
              </Button>
            )}
          </Actions>
        </MarketplaceTeaser>
      )}
      <CatalogWrapper>
        {marketplaceBidders.map((bidder) => {
          const header = marketPlaceHeaders[bidder.id]
          return (
            <ModuleCard
              key={bidder.id}
              baseCDNUrl={STATIC_ASSETS_CDN}
              code={bidder.id}
              name={bidder.name}
              type={'prebidBidder'}
              status={
                <ModuleFooters>
                  <div>
                    <div>{header.value}</div>
                  </div>
                </ModuleFooters>
              }
              footerIcon={<></>}
              topLeftIcon={header.labelInfo}
              onClick={header.onClick}
            />
          )
        })}
      </CatalogWrapper>
    </PageContent>
  )
}
